/* eslint-disable prefer-destructuring */
import React, { MouseEvent, useCallback, useState } from 'react';
import { Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  AlignStartColumn,
  AlignStartRow,
} from 'spw-shared/globalComponents/grid';
import {
  BigKey,
  MixBarHeader,
} from 'spw-programming-projects/program-summary/components/styles';
import { spaceFactorMap as rawSpaceFactorMap } from 'spw-programming-projects/program-summary/macro-single-page-summary/utils';
import { BoldValue } from 'spw-programming-projects/program-summary/macro-single-page-summary/styles';
import {
  PaddedAlignEndColumn,
  PercentBarContainer,
} from 'spw-programming-projects/program-summary/single-page-summary/styles';
import {
  numberWithCommas,
  asPercentageRounded,
  asUsfOrZero,
} from 'spw-shared/utils';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';
import {
  SpacePercentBar,
  UsfMixColumn,
} from 'spw-components/project-details-tabs/program-summary/esa-single-page-summary/styles';
import { selectSelectedLevelsExpanded } from 'spw-redux/modules/program-summary/esa/selectors';
import {
  IProjectColors,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import { UpdateSpacesByTypeParams } from 'spw-redux/modules/program-summary/types';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import {
  Category,
  IProjectSpaceType,
  Subcategory,
} from 'spw-redux/modules/space_types/types';
import { SpaceIcon } from 'spw-projects/components/space-type-icons';
import { selectCurrentSpaceTypeColors } from 'spw-redux/modules/space_types/selectors';
import { omit } from 'ramda';
import { BulkEditButton } from '../../../components/styles';
import AvgCardPopover from './avg-card-popover';

const SpaceBar = ({
  percentage,
  color,
}: {
  percentage: number;
  color: string;
}) => (
  <PercentBarContainer>
    <SpacePercentBar width={`${percentage * 100}%`} color={color} />
  </PercentBarContainer>
);

export const UsfMixBar = ({
  spaceType,
  spaceTypeId,
  spaceName,
  percentage,
  usf,
  usfAvg,
  icon,
  onClickSpaceIcon,
  projectColors,
  projectSpaceTypes,
  projectSpaceTypeCategories,
  category,
  onUpdateAvgUsf,
  viewOnly,
  clientFacingNamesAreVisible,
}: {
  spaceType: string;
  spaceTypeId: number;
  spaceName: string;
  percentage: number;
  usf: number;
  usfAvg: number;
  icon: string;
  onClickSpaceIcon?: (e: Element) => void;
  projectColors: IProjectColors;
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[];
  category: Category | Subcategory;
  onUpdateAvgUsf?: (payload: UpdateSpacesByTypeParams) => void;
  viewOnly: boolean;
  clientFacingNamesAreVisible?: boolean;
}) => {
  const { convertArea, getUnit, measurementSystem } = useUnitConversion();
  const { allSelectedFloorIds } = useSelector(selectSelectedLevelsExpanded);
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const currentSpaceTypeColors = useSelector(selectCurrentSpaceTypeColors);

  const spaceFactorMap = rawSpaceFactorMap(projectColors);
  // Default to 0 if NaN
  const percentageFinal = percentage || 0;

  let factors = omit(['color'], spaceFactorMap[spaceType]);

  if (!factors) {
    factors = {
      title: spaceName,
    };
  }

  const projectSpaceType = projectSpaceTypes?.[spaceTypeId];

  const topLevelProjectSpaceTypeCategory = projectSpaceTypeCategories.find(
    (pstc) =>
      pstc.defaultSpaceTypeCategory.category === category ||
      pstc.subcategories.find(
        (subcategory) =>
          subcategory.defaultSpaceTypeCategory.category === category
      )
  );

  const projectSpaceTypeCategory = projectSpaceTypes[spaceTypeId]?.category;

  const handleAvgUsfClick = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleSaveAvgUSF = useCallback(
    (value: number, callback: () => void) => {
      onUpdateAvgUsf({
        spaceTypeId,
        usf: value,
        floorIds: allSelectedFloorIds,
        callback,
      });
    },
    [onUpdateAvgUsf, spaceTypeId, allSelectedFloorIds]
  );

  const name = clientFacingNamesAreVisible
    ? projectSpaceType?.clientFacingName || spaceName
    : spaceName;

  return (
    <Grid container>
      <Grid item xs={6}>
        <AlignStartColumn>
          <MixBarHeader
            data-testid={`space-type-name-${projectSpaceType?.spaceType?.spaceType}`}
            style={{ paddingTop: '8px', paddingBottom: '4px' }}
          >
            {name}
          </MixBarHeader>
          <AlignStartRow justifyContent="start">
            <SpaceIcon
              disabled={viewOnly}
              spaceTypeId={spaceTypeId}
              topLevelProjectSpaceTypeCategoryId={
                topLevelProjectSpaceTypeCategory?.id
              }
              projectSpaceTypeCategoryId={projectSpaceTypeCategory?.id}
              projectSpaceTypeId={projectSpaceTypes[spaceTypeId]?.id}
              onClick={onClickSpaceIcon}
              iconName={projectSpaceTypes[spaceTypeId]?.icon || icon}
              defaultIconName={icon}
              name={factors.title}
            />
            <SpaceBar
              percentage={percentageFinal}
              color={currentSpaceTypeColors[projectSpaceType?.id]?.color}
            />
          </AlignStartRow>
        </AlignStartColumn>
      </Grid>
      <Grid item xs={6}>
        <UsfMixColumn>
          <Grid item xs={4}>
            <PaddedAlignEndColumn>
              <BoldValue data-testid="percentage">
                {asPercentageRounded(percentageFinal, false)}
              </BoldValue>
              <BigKey>{getUnit('area')}</BigKey>
            </PaddedAlignEndColumn>
          </Grid>
          <Grid item xs={4} style={{ paddingRight: '16px' }}>
            <PaddedAlignEndColumn>
              <BoldValue data-testid="quantity">
                {asUsfOrZero(convertArea(usf))}
              </BoldValue>
              <BigKey>{getUnit('area')}</BigKey>
            </PaddedAlignEndColumn>
          </Grid>
          <Grid item xs={4} style={{ borderLeft: '1px solid #E0E0E0' }}>
            <BulkEditButton
              onClick={!viewOnly ? handleAvgUsfClick : undefined}
              className={anchorEl ? 'selected' : ''}
              $disableHoverState={viewOnly}
            >
              <BoldValue data-testid="average">
                {numberWithCommas(convertArea(usfAvg), false, true)}
              </BoldValue>
              <div className="label">Avg {getUnit('area')}</div>
            </BulkEditButton>
            {!!anchorEl && (
              <AvgCardPopover
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                subject="USF"
                spaceTypeName={name}
                texts={[
                  `Changing this value updates the ${getUnit(
                    'area'
                  )} for all of these spaces that are located on the floors selected in this Program Summary.`,
                  'To modify which floors are affected by this change, select floors from the dropdown at the top of this page before saving this updated value.',
                ]}
                onSave={handleSaveAvgUSF}
                max={50000}
                min={0}
                allowDecimals={measurementSystem === MeasurementSystem.metric}
              />
            )}
          </Grid>
        </UsfMixColumn>
      </Grid>
    </Grid>
  );
};
